
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type EntryType = Database["public"]["Enums"]["entry_type"];

export type Entry = {
  id: string;
  title: string;
  content: string | null;
  entry_type: EntryType;
  creator_id: string;
  creator_email?: string;
  is_favorite: boolean;
  is_pinned?: boolean;
  password?: string | null;
  share_id: string;
  updated_at: string;
  is_shared_entry?: boolean; // New flag to identify shared entries
};

export async function fetchPublicEntries(currentUserId: string | undefined): Promise<Entry[]> {
  try {
    console.log("Fetching public entries, current user ID:", currentUserId);
    
    // Fetch public entries not created by the current user
    const { data: publicEntries, error } = await supabase
      .from("entries")
      .select("*")
      .eq("is_public", true)
      .neq("creator_id", currentUserId || "dummy-id") // Use a dummy ID if currentUserId is undefined
      .order("updated_at", { ascending: false });

    if (error) {
      console.error("Error in fetchPublicEntries:", error);
      throw error;
    }

    console.log("Public entries fetched:", publicEntries?.length || 0);

    if (!publicEntries || publicEntries.length === 0) {
      return [];
    }

    // Get creator information for each entry
    const entriesWithCreators = await Promise.all(
      publicEntries.map(async (entry) => {
        // Get creator profile info
        const { data: creatorData, error: creatorError } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", entry.creator_id)
          .single();

        if (creatorError) {
          console.error("Error fetching creator info:", creatorError);
          return {
            ...entry,
            creator_email: "Unknown User",
          };
        }

        return {
          ...entry,
          creator_email: creatorData?.full_name || "Unknown User",
        };
      })
    );

    console.log("Entries with creators:", entriesWithCreators.length);
    return entriesWithCreators;
  } catch (error) {
    console.error("Error fetching public entries:", error);
    throw error;
  }
}

export async function searchPublicEntries(
  searchQuery: string,
  currentUserId: string | undefined
): Promise<Entry[]> {
  try {
    console.log("Searching public entries:", searchQuery);
    // Search public entries by title and content
    const { data: searchResults, error } = await supabase
      .from("entries")
      .select("*")
      .eq("is_public", true)
      .neq("creator_id", currentUserId || "dummy-id") // Use dummy ID if currentUserId is undefined
      .or(`title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`)
      .order("updated_at", { ascending: false });

    if (error) {
      console.error("Error in searchPublicEntries:", error);
      throw error;
    }

    console.log("Search results:", searchResults?.length || 0);

    if (!searchResults || searchResults.length === 0) {
      return [];
    }

    // Get creator info for search results
    const entriesWithCreators = await Promise.all(
      searchResults.map(async (entry) => {
        const { data: creatorData } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", entry.creator_id)
          .single();

        return {
          ...entry,
          creator_email: creatorData?.full_name || "Unknown User",
        };
      })
    );

    return entriesWithCreators;
  } catch (error) {
    console.error("Error searching public entries:", error);
    throw error;
  }
}

// Function to strip HTML tags for display in card
export function stripHtmlTags(html: string | null): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "");
}

// New function to check if an entry is a shared entry that was added to personal dashboard
export async function isSharedEntry(entryId: string): Promise<boolean> {
  // Look for entries with titles ending with " (Shared)"
  const { data, error } = await supabase
    .from("entries")
    .select("title")
    .eq("id", entryId)
    .single();
    
  if (error || !data) {
    console.error("Error checking if entry is shared:", error);
    return false;
  }
  
  return data.title.endsWith(" (Shared)");
}

// New function to remove a shared entry from personal dashboard
export async function removeSharedEntry(entryId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("entries")
      .delete()
      .eq("id", entryId);
      
    if (error) {
      console.error("Error removing shared entry:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error in removeSharedEntry:", error);
    return false;
  }
}
